/**
 * @file Guarded route renderer and route-level composition for Ordering.
 */

import { state } from "../../app/state/state.js";
import { hasOrderingAccess } from "../../app/routing/boardAccess.js";
import { renderMobileRouteHeaderActions } from "../../shared/ui/mobileRouteHeaderActions.js";
import { attachOrderingRouteListeners } from "./orderingListeners.js";
import {
    beginOrderingRouteSession,
    getOrderingState,
} from "./orderingState.js";
import { renderOrderingBoard } from "./orderingBoard.js";
import { closeOrderingModalForTeardown } from "./orderingModal.js";
import { invalidateOrderingStatusMutations } from "./orderingMutations.js";

let activeGeneration = null;

/**
 * Renders the guarded Ordering route and attaches its route-only listeners.
 * @returns {void}
 */
export function renderOrderingView() {
    const root = document.getElementById("ordering-view");
    const board = document.getElementById("ordering-board");
    if (!root || !board) {return;}

    if (!hasOrderingAccess(state.currentUserProfile)) {
        closeOrderingModalForTeardown();
        invalidateOrderingStatusMutations();
        board.innerHTML = `<div class="rounded-lg border border-red-200 bg-red-50 p-6"><h3 class="font-semibold text-red-800">Ordering access denied</h3><p class="mt-1 text-sm text-red-700">Your account does not have Ordering permission.</p></div>`;
        return;
    }

    root.querySelector("[data-mobile-route-header-actions-slot]")?.replaceChildren();
    const mobileActions = root.querySelector("[data-mobile-route-header-actions-slot]");
    if (mobileActions) {mobileActions.innerHTML = renderMobileRouteHeaderActions();}

    const ordering = getOrderingState();
    if (activeGeneration === ordering.routeGeneration && activeGeneration !== null) {
        renderOrderingBoard(activeGeneration);
        return;
    }

    activeGeneration = beginOrderingRouteSession();
    renderOrderingBoard(activeGeneration);
    attachOrderingRouteListeners(activeGeneration, () =>
        renderOrderingBoard(activeGeneration));
}

/**
 * Renders the active Ordering session without starting a new listener set.
 * @returns {void}
 */
export function renderActiveOrderingView() {
    if (activeGeneration === null) {return;}
    const ordering = getOrderingState();
    if (activeGeneration !== ordering.routeGeneration) {return;}
    renderOrderingBoard(activeGeneration);
}

/**
 * Releases the active route marker so a later entry attaches fresh listeners.
 * @returns {void}
 */
export function deactivateOrderingView() {
    closeOrderingModalForTeardown();
    invalidateOrderingStatusMutations();
    activeGeneration = null;
}
